import React from 'react';
import {
  Platform,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
} from 'react-native';
import { useController } from 'react-hook-form';

import { FormGroupMatrixProps } from '@nocode/types/formGroup.type';
import { isCanvas } from '@common/utils';
import StarIconFilled from '@common/components/Icons/StarIconFilled';
import StarIconOutlined from '@common/components/Icons/StarIconOutlined';

const Label = ({
  text,
  styleConfig,
  style,
}: {
  styleConfig: FormGroupMatrixProps['styleConfig'];
  text: string;
  style?: TextStyle;
}) => (
  <Text
    style={[
      {
        textAlign: 'center',
        minWidth: 48,
        color: styleConfig?.label?.color,
        fontSize: styleConfig?.label?.fontSize || 14,
      },
      style,
    ]}
  >
    {text}
  </Text>
);

const CustomRadioButton = ({
  design = 'radio',
  selectedValue,
  styleConfig,
  value,
  handlePress,
}: {
  selectedValue: number;
  design?: FormGroupMatrixProps['design'];
  start: number;
  value: number;
  styleConfig: FormGroupMatrixProps['styleConfig'];
  handlePress: (value: number) => void;
}) => {
  const active =
    design === 'radio' ? selectedValue === value : selectedValue >= value;
  const activeColor = styleConfig?.activeColor || '#0175FF';
  const inactiveColor = styleConfig?.inactiveColor || '#ADB6B9';

  return (
    <TouchableOpacity
      style={{ padding: 12 }}
      onPress={() => handlePress(value)}
    >
      {design === 'radio' ? (
        <View
          style={{
            width: 16,
            height: 16,
            borderWidth: 1,
            borderColor: active ? activeColor : inactiveColor,
            borderRadius: 8,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {active ? (
            <View
              style={{
                height: 8,
                width: 8,
                borderRadius: 4,
                backgroundColor: active ? activeColor : inactiveColor,
              }}
            />
          ) : null}
        </View>
      ) : null}

      {design === 'star' ? (
        <View style={{ height: 16, width: 16 }}>
          {active ? (
            <StarIconFilled fill={activeColor} />
          ) : (
            <StarIconOutlined fill={inactiveColor} />
          )}
        </View>
      ) : null}

      {design === 'step' ? (
        <View style={{ position: 'relative' }}>
          <View
            style={{
              height: 12,
              width: 12,
              backgroundColor: active ? activeColor : inactiveColor,
              borderRadius: 6,
            }}
          />
          {value !== 1 ? (
            <View
              style={{
                height: 2,
                width: 24,
                backgroundColor: active ? activeColor : inactiveColor,
                position: 'absolute',
                top: 5,
                right: 12,
              }}
            />
          ) : null}
        </View>
      ) : null}
    </TouchableOpacity>
  );
};

const FormGroupMatrix = ({
  name,
  design,
  options,
  styleConfig,
  startText = 'あてはまらないと思う',
  endText = 'あてはまると思う',
  range,
}: FormGroupMatrixProps) => {
  const canvas = isCanvas() && Platform.OS === 'web';
  const newLineLabel = options?.length >= 6;

  const {
    field,
    fieldState: { invalid, isTouched, isDirty },
    formState: { touchedFields, dirtyFields },
  } = useController({
    name,
    defaultValue: '',
  });

  const handlePress = (value: number) => {
    if (canvas) {
      return;
    }

    field.onChange(value);
  };

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: newLineLabel ? 'column' : 'row',
      }}
    >
      {!newLineLabel ? (
        <Label text={startText} styleConfig={styleConfig} />
      ) : null}

      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          paddingHorizontal: 16,
          justifyContent: 'center',
        }}
      >
        {options?.map((item) => (
          <View
            key={item.label}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                color: styleConfig?.point?.color,
                fontSize: styleConfig?.point?.fontSize,
              }}
            >
              {item.label}
            </Text>
            <CustomRadioButton
              value={item.value as number}
              handlePress={handlePress}
              design={design}
              selectedValue={field.value}
              start={range.start as number}
              styleConfig={styleConfig}
            />
          </View>
        ))}
      </View>
      {!newLineLabel ? (
        <Label text={endText} styleConfig={styleConfig} />
      ) : null}
      {newLineLabel ? (
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Label
            text={startText}
            styleConfig={styleConfig}
            style={{ flex: 1, maxWidth: 100 }}
          />
          <Label
            text={endText}
            styleConfig={styleConfig}
            style={{ flex: 1, maxWidth: 100 }}
          />
        </View>
      ) : null}
    </View>
  );
};

export default FormGroupMatrix;
